const toStr = (v) => (v === null || v === undefined ? "" : String(v));

const toNumber = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(toStr(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

const toCents = (v) => {
  const n = toNumber(v);
  if (n === null) return null;
  // Decimal-safe rounding to cents.
  return Math.round((n + Number.EPSILON) * 100);
};

const fromCents = (c) => (typeof c === "number" ? c / 100 : null);

const isLocalhostDev = () => {
  try {
    return (
      process.env.NODE_ENV !== "production" &&
      typeof window !== "undefined" &&
      window.location &&
      window.location.hostname === "localhost"
    );
  } catch (_e) {
    return false;
  }
};

/**
 * Build VAT breakdown rows using backend-provided fields when available.
 * Strategy:
 * - Prefer backend totals for the final truth (`grandTotalWithoutVat`, `vatTotal`, `grandTotal`)
 * - Use backend per-rate fields (`vat{rate}Base`, `vat{rate}`, `vat{rate}Total`) when present
 * - If per-rate rows don't sum to totals (common 0.01 drift), adjust the last row so sums match.
 *
 * This prevents contradictory UI like:
 * - "DPH 5% = 1.05 €" but "Spolu DPH = 1.04 €"
 */
export const buildVatBreakdownRows = ({ order, vatRates }) => {
  const baseTotalC = toCents(order?.grandTotalWithoutVat);
  const vatTotalC = toCents(order?.vatTotal);
  const totalC = toCents(order?.grandTotal);

  const rows = (Array.isArray(vatRates) ? vatRates : [])
    .map((r) => {
      const key = r?.key; // e.g. "vat5"
      const rate = r?.value; // e.g. 5
      if (!key) return null;

      const baseC = toCents(order?.[`${key}Base`]);
      const vatC = toCents(order?.[`${key}`]);
      const totalRateC = toCents(order?.[`${key}Total`]);

      // Show this vat rate row if any of its parts exists and is non-zero.
      const anyC = [baseC, vatC, totalRateC].some(
        (x) => typeof x === "number" && x !== 0,
      );
      if (!anyC) return null;

      // Prefer explicit total; otherwise base+vat if both exist.
      const computedTotalC =
        typeof totalRateC === "number"
          ? totalRateC
          : typeof baseC === "number" && typeof vatC === "number"
            ? baseC + vatC
            : null;

      return {
        key,
        rate: typeof rate === "number" ? rate : null,
        baseC,
        vatC,
        totalC: computedTotalC,
      };
    })
    .filter(Boolean)
    // sort by numeric rate asc if present
    .sort((a, b) => (a.rate ?? 0) - (b.rate ?? 0));

  // If we have no per-rate rows, we can't show a breakdown.
  if (rows.length === 0) {
    return {
      rows: [],
      totals: { baseTotalC, vatTotalC, totalC },
      adjusted: false,
    };
  }

  const sum = (field) =>
    rows.reduce(
      (acc, r) => acc + (typeof r[field] === "number" ? r[field] : 0),
      0,
    );

  const sumBaseC = sum("baseC");
  const sumVatC = sum("vatC");
  const sumTotalC = sum("totalC");

  // If totals are missing, we just return backend breakdown as-is (still consistent within itself).
  if (
    typeof baseTotalC !== "number" ||
    typeof vatTotalC !== "number" ||
    typeof totalC !== "number"
  ) {
    return {
      rows,
      totals: { baseTotalC, vatTotalC, totalC },
      adjusted: false,
    };
  }

  const diffBase = baseTotalC - sumBaseC;
  const diffVat = vatTotalC - sumVatC;
  const diffTotal = totalC - sumTotalC;

  const needsAdjust = diffBase !== 0 || diffVat !== 0 || diffTotal !== 0;
  if (!needsAdjust) {
    return {
      rows,
      totals: { baseTotalC, vatTotalC, totalC },
      adjusted: false,
    };
  }

  // Adjust the last row to match totals exactly.
  const last = rows[rows.length - 1];
  const adjustedLast = {
    ...last,
    baseC: typeof last.baseC === "number" ? last.baseC + diffBase : last.baseC,
    vatC: typeof last.vatC === "number" ? last.vatC + diffVat : last.vatC,
    totalC:
      typeof last.totalC === "number" ? last.totalC + diffTotal : last.totalC,
  };

  // Keep arithmetic consistent if we can (base + vat = total).
  if (
    typeof adjustedLast.baseC === "number" &&
    typeof adjustedLast.vatC === "number"
  ) {
    adjustedLast.totalC = adjustedLast.baseC + adjustedLast.vatC;
  }

  const nextRows = rows.slice(0, -1).concat(adjustedLast);

  if (isLocalhostDev()) {
    // Non-intrusive dev diagnostics.
    // eslint-disable-next-line no-console
    console.warn("[DSOKOrderForm] VAT breakdown drift adjusted", {
      baseTotal: fromCents(baseTotalC),
      vatTotal: fromCents(vatTotalC),
      total: fromCents(totalC),
      sumBase: fromCents(sumBaseC),
      sumVat: fromCents(sumVatC),
      sumTotal: fromCents(sumTotalC),
      diffBase: fromCents(diffBase),
      diffVat: fromCents(diffVat),
      diffTotal: fromCents(diffTotal),
      usedKeys: nextRows.map((r) => r.key),
    });
  }

  return {
    rows: nextRows,
    totals: { baseTotalC, vatTotalC, totalC },
    adjusted: true,
  };
};

export const formatMoneyFromCents = (toFixed, cents, currencyCode) => {
  if (typeof cents !== "number") return "—";
  return `${toFixed(fromCents(cents), 2)} ${currencyCode || ""}`.trim();
};
